import { memo, useContext, useMemo } from 'react'
import './index.scss'
import SidebarContext from '../../contexts/SidebarContext/context'
import Icon from '../Icon'
import { closeInfobar, closeSidebar, openInfobar, openSidebar } from '../../contexts/SidebarContext/actions'
import { ICON } from '../Icon/data'
import { useWindowSize } from '../../hooks/useWindowSize'

type Sidebar = {
  type?: 'sidebar' | 'infobar'
}

const MODAL_MAX_WIDTH = 671;

const Sidebar = memo(({ type = 'sidebar' }: Sidebar) => {
  const { sidebar, dispatch } = useContext(SidebarContext)
  const { size, isFullSize } = useWindowSize()

  const bar = useMemo(() => type === 'sidebar' ? sidebar : sidebar.info_sidebar, [type, sidebar])

  const classNames = useMemo(() => {
    const classArr: string[] = [type]
    if (bar?.isOpen) classArr.push('open')
    if (!isFullSize) classArr.push('modal-sidebar')
    return classArr.join(' ') 
  }, [bar?.isOpen, type])

  const sidebarWidth = useMemo(() => {
    if (isFullSize) return bar?.width
    return size.width > MODAL_MAX_WIDTH ? MODAL_MAX_WIDTH : size.width - 32
  }, [size, isFullSize, bar?.width])
  
  const styles = useMemo(() => {
    const defaultRight = isFullSize ? '0px' : '16px'
    const styleObj = { width: `${sidebarWidth}px`, right: defaultRight }
    if (!bar?.isOpen) {
      styleObj.right = `calc((${sidebarWidth}px + 0.5rem) * -1)`
    }
    return styleObj
  }, [sidebarWidth, bar?.isOpen])

  if (!bar?.content) return null

  const onClick = () => {
    if (bar.isOpen) {
      if (type === 'infobar') dispatch(closeInfobar())
      else dispatch(closeSidebar())
    } else {
      if (type === 'infobar') dispatch(openInfobar())
      else dispatch(openSidebar())
    }
  }

  return (
    <>
      {(type === 'infobar' || !isFullSize) && bar.isOpen && (
        <div className="Blackout" style={{ zIndex: type === 'infobar' ? 3 : 2 }} />
      )}
      <div className={`Sidebar ${classNames}`} style={styles}>
        {type === 'sidebar' && isFullSize && (
          <div
            className="Sidebar__control"
            role="button"
            tabIndex={0}
            onClick={() => onClick()}
          >
            <Icon
              icon={bar?.isOpen ? ICON.CHEVRON_RIGHT : ICON.CHEVRON_LEFT}
              color="#ffffff"
              size={16}
            />
          </div>
        )}
        <div className="Sidebar__content">
          {bar.header && (
            <div className="Sidebar__header">
              {bar.header}
              <div
                className="Sidebar__header-close"
                role="button"
                tabIndex={0}
                onClick={() => onClick()}
              >
                <Icon icon={ICON.CLOSE} />
              </div>
            </div>
          )}
          <div className="Sidebar__content-main">{bar?.content}</div>
        </div>
      </div>
    </>
  )
})

export default Sidebar
